import React, { useState } from 'react';

const DoctorCompareRow = ({ doctor }) => (
  <div className="text-sm space-y-1">
    <div>
      <span className="font-medium text-gray-600">Name:</span>
      <span className="ml-2 text-gray-900">{doctor?.primary_name || 'N/A'}</span>
    </div>
    <div>
      <span className="font-medium text-gray-600">Speciality:</span>
      <span className="ml-2 text-gray-700">{doctor?.speciality || 'N/A'}</span>
    </div>
    {doctor?.service_provided && (
      <div>
        <span className="font-medium text-gray-600">Services:</span>
        <p className="mt-1 text-gray-600 text-xs line-clamp-3">{doctor.service_provided}</p>
      </div>
    )}
  </div>
);

const Step3_Complete = ({ summary, clid, onRestart }) => {
  const createdDoctors = summary?.createdDoctors || 0;
  const updatedDoctors = summary?.updatedDoctors || [];
  const targetClid = summary?.clid || clid || '';
  const errors = summary?.errors || [];
  const [expandedUpdated, setExpandedUpdated] = useState({});

  if (!summary?.success) {
    return (
      <div className="space-y-6 text-center">
        <h2 className="text-2xl font-bold text-gray-900">Step 3: Complete</h2>
        <p className="text-gray-600">No successful import. Please restart and try again.</p>
        <button
          type="button"
          onClick={onRestart}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Start New Import
        </button>
      </div>
    );
  }

  const handleViewDoctors = () => {
    if (!targetClid) return;
    window.location.href = `/clinics/${targetClid}#doctors`;
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 3: Complete</h2>
        <p className="text-gray-600">Doctors imported. No schedules were created.</p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center space-y-2">
        <p className="font-semibold text-green-800 text-lg">Import Completed</p>
        <p className="text-green-900">
          {createdDoctors > 0 && (
            <>Created {createdDoctors} doctor{createdDoctors === 1 ? '' : 's'}.</>
          )}
          {createdDoctors > 0 && updatedDoctors.length > 0 && ' '}
          {updatedDoctors.length > 0 && (
            <>Replaced {updatedDoctors.length} doctor{updatedDoctors.length === 1 ? '' : 's'}.</>
          )}
          {createdDoctors === 0 && updatedDoctors.length === 0 && 'No doctors imported.'}
        </p>
        {targetClid && (
          <p className="text-sm text-green-800">
            Clinic: <span className="font-mono text-green-900">{targetClid}</span>
          </p>
        )}
        {errors.length > 0 && (
          <div className="mt-2 text-left text-sm text-yellow-800">
            <p className="font-medium">Some items had issues:</p>
            <ul className="list-disc list-inside">
              {errors.map((e, i) => (
                <li key={i}>{`${e.doctor || 'Unknown'}: ${e.error}`}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {updatedDoctors.length > 0 && (
        <div className="border rounded-lg p-4 bg-white">
          <h3 className="font-semibold text-gray-900 mb-3">Replaced doctors (original vs updated)</h3>
          <div className="space-y-4">
            {updatedDoctors.map((u, i) => {
              const isExp = expandedUpdated[i];
              return (
                <div key={i} className="border rounded p-3">
                  <button
                    type="button"
                    onClick={() => setExpandedUpdated((p) => ({ ...p, [i]: !p[i] }))}
                    className="w-full text-left font-medium text-gray-900"
                  >
                    {u.name}
                    <span className="ml-2 text-sm text-gray-500">{isExp ? 'Hide' : 'Show'} comparison</span>
                  </button>
                  {isExp && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 rounded">
                        <h4 className="font-medium text-gray-700 mb-2">Original</h4>
                        <DoctorCompareRow doctor={u.original} />
                      </div>
                      <div className="p-3 bg-blue-50 rounded border border-blue-200">
                        <h4 className="font-medium text-blue-800 mb-2">Updated</h4>
                        <DoctorCompareRow doctor={u.updated} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-4">
        <button
          type="button"
          onClick={onRestart}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Start New Import
        </button>
        {targetClid && (
          <button
            type="button"
            onClick={handleViewDoctors}
            className="px-8 py-3 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            View Doctors
          </button>
        )}
      </div>
    </div>
  );
};

export default Step3_Complete;
